import { useSpring } from "@react-spring/web";
import classcat from "classcat";
import React, {
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedCallback } from "use-debounce";

import {
  IconBasket,
  IconExclamationCircle,
  IconTickCircle,
} from "@touchtech/web-baselayer-ui";

import {
  SCREEN_BREAKPOINTS,
  SEARCH_DRAWER_WIDTH,
  SELECTION_COLOR_VARIANT_INFO,
  TranslationContext,
} from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import {
  useCollections,
  useConfiguration,
  useManageBasket,
  useScreenOrientation,
  useStyleDetails,
  useWidget,
} from "@is/hooks";
import { TrackingEventTypes, trackEvent } from "@is/services/EventTracking";
import {
  BasketItemPrice,
  ContentItemType,
  MediaOrderingRule,
  ProductItem,
  WidgetItem,
  WidgetType,
} from "@is/types";
import {
  convertSizeBasedOnResolution,
  createWidgetItem,
  getAllSizesAndLengths,
  getAttributesWithStyleLevelAttributes,
  getSizesAndLengthsOfSelectedColorVariant,
  getSizesAsFilters,
  getStylePriceRange,
  sortProductMedia,
} from "@is/utils";

import css from "../ProductDetailsItem.module.css";

type Props = {
  item: ProductItem;
  itemIndex: number;
  sectionId: string;
};

const BASKET_BUTTON_CHANGE_STATE_TIMEOUT = 2000;

const ANIMATION_OVERLAY_TIMEOUT = 300;

const ANIMATION_CLOSE_PDP = 500;

const ADDITIONAL_TOP_OFFSET = 100;

const COLOR_VARIANTS_SIDEMARGIN = 80;
const COLOR_VARIANTS_GAP = 22;
const COLOR_VARIANT_WIDTH = 72;
const COLOR_VARIANT_HEIGHT = 96;
const COLOR_VARIANTS_INITIAL_ROWS = 2;

const COLOR_DEFAULT = "default";

const sortWidgets = (a) => (a.type === WidgetType.HowToWearIt ? -1 : 1);

const useManageProductDetails = ({ item, itemIndex, sectionId }: Props) => {
  const { t } = useTranslation(TranslationContext.ProductDetails);

  const {
    items,
    updateViewState,
    lastScannedValue,
    isCheckoutEnabled,
    sections,
  } = useContext(ViewStateContext).content;

  const [selectedSize, setSelectedSize] = useState<string | null>(
    item.preselectedSize?.size || null
  );
  const [selectedLength, setSelectedLength] = useState<string | null>(
    item.preselectedSize?.length || null
  );
  const [selectedColorVariantId, setSelectedColorVariantId] = useState<string>(
    item.id
  );
  const [showSizeError, setShowSizeError] = useState<boolean>(false);
  const [isAdded, setIsAdded] = useState<boolean>(false);
  const [showOutOfStockAlarm, setShowOutOfStockAlarm] =
    useState<boolean>(false);

  const [showMoreColorVariants, setShowMoreColorVariants] =
    useState<boolean>(false);

  const [widgets, setWidgets] = useState<WidgetItem[]>([]);
  const [isCollectionModalOpen, setIsCollectionModalOpen] =
    useState<boolean>(false);

  const { isPortraitView, isLandscapeView } = useScreenOrientation();
  const { collections } = useCollections({ filterOutOfStockItems: false });

  const { attributes } = useConfiguration({
    revalidateIfStale: true,
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    shouldRetryOnError: true,
  });

  const animationOverlayRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const howToWearItRef = useRef<HTMLDivElement>(null);

  const [styles, api] = useSpring(() => ({
    top: 0,
    left: 0,
    config: { duration: 300 },
  }));

  const [, getWidget, getHTWIWidget] = useWidget();

  const { data } = useStyleDetails(
    item.styleId,
    item.searchSizesAndLengths,
    !!item.styleDetails
  );

  const { updateBasket, basketItems, isPending } = useManageBasket();

  const styleDetails = data ?? item.styleDetails;
  const variants = useMemo(() => styleDetails?.variants || [], [styleDetails]);

  const materialCompositionAndProductDescriptionEventData = useMemo(
    () => ({
      styleId: item.styleId,
      variantId: selectedColorVariantId,
    }),
    [item, selectedColorVariantId]
  );

  const selectedColorVariant = useMemo(
    () =>
      styleDetails?.variants.find(({ id }) => id === selectedColorVariantId) ??
      styleDetails?.variants[0],
    [selectedColorVariantId, styleDetails]
  );

  const isLastItem = useMemo(
    () => itemIndex === items.length - 1,
    [items, itemIndex]
  );

  const notOnlyItemInSection = useMemo(
    () =>
      sections.find((section) => section.id === sectionId)?.items.length > 1,
    [sectionId, sections]
  );

  const isLengthRequired = useMemo(
    () =>
      selectedColorVariant?.sizeInfos?.some((sizeInfo) => !!sizeInfo.length),
    [selectedColorVariant]
  );

  const getSizesAndLengths = useCallback(
    (size?: string) =>
      getSizesAndLengthsOfSelectedColorVariant(selectedColorVariant, size),
    [selectedColorVariant]
  );

  const { sizes, lengths } = useMemo(
    () => getAllSizesAndLengths(variants),
    [variants]
  );

  const availableVariantsWithSelectedSize = useMemo(() => {
    if (!selectedSize || (isLengthRequired && !selectedLength)) {
      return [];
    }

    return variants.reduce((acc: string[], variant) => {
      const sizes = variant.sizeInfos;

      const moreItemsLeft = sizes.some((info) => {
        const basketItem = basketItems?.find((item) => item.ean === info.ean);

        const itemsLeft =
          (info.onlineQuantity ?? 0) +
          (info.instoreQuantity ?? 0) -
          (basketItem?.quantity ?? 0);

        return (
          info.size === selectedSize &&
          (!isLengthRequired || info.length === selectedLength) &&
          itemsLeft > 0
        );
      });

      if (moreItemsLeft) {
        acc.push(variant.id);
      }

      return acc;
    }, []);
  }, [variants, basketItems, selectedSize, selectedLength, isLengthRequired]);

  const { availableSizesForColorVariant, availableLengthsForColorVariant } =
    useMemo(() => {
      if (!selectedColorVariant) {
        return {
          availableSizesForColorVariant: [],
          availableLengthsForColorVariant: [],
        };
      }

      const sizeInfos = selectedColorVariant?.sizeInfos;

      return sizeInfos?.reduce(
        (acc, info) => {
          const basketItem = basketItems?.find(
            (basketItem) => basketItem.ean === info.ean
          );

          const totalAvailableQuantity =
            (info.onlineQuantity ?? 0) +
            (info.instoreQuantity ?? 0) -
            (basketItem?.quantity ?? 0);

          const sizeMatchesSelectedLength =
            !selectedLength || info.length === selectedLength;

          if (
            info.size &&
            !acc.availableSizesForColorVariant.includes(info.size) &&
            totalAvailableQuantity > 0 &&
            sizeMatchesSelectedLength
          ) {
            acc.availableSizesForColorVariant.push(info.size);
          }

          const lengthMatchesSelectedSize =
            !selectedSize || info.size === selectedSize;

          if (
            info.length &&
            !acc.availableLengthsForColorVariant.includes(info.length) &&
            totalAvailableQuantity > 0 &&
            lengthMatchesSelectedSize
          ) {
            acc.availableLengthsForColorVariant.push(info.length);
          }

          return acc;
        },
        {
          availableSizesForColorVariant: [],
          availableLengthsForColorVariant: [],
        }
      );
    }, [selectedColorVariant, basketItems, selectedSize, selectedLength]);

  const selectedSizeInfo = useMemo(() => {
    if (!selectedColorVariant?.sizeInfos?.length) {
      return null;
    }

    if (!selectedSize || (isLengthRequired && !selectedLength)) {
      return null;
    }

    let selectedSizeInfo;

    if (isLengthRequired) {
      selectedSizeInfo = selectedColorVariant?.sizeInfos?.find(
        (sizeInfo) =>
          sizeInfo.size === selectedSize && sizeInfo.length === selectedLength
      );
    } else {
      selectedSizeInfo = selectedColorVariant?.sizeInfos?.find(
        (sizeInfo) => sizeInfo.size === selectedSize
      );
    }

    if (selectedSizeInfo) {
      const basketItem = basketItems?.find(
        (item) => item.ean === selectedSizeInfo.ean
      );

      return {
        ...selectedSizeInfo,
        availableToOrder:
          (selectedSizeInfo.onlineQuantity ?? 0) - (basketItem?.quantity || 0),
      };
    }

    return {
      ean: "",
      instoreQuantity: 0,
      onlineQuantity: 0,
      availableToOrder: 0,
    };
  }, [
    selectedColorVariant,
    selectedSize,
    selectedLength,
    isLengthRequired,
    basketItems,
  ]);

  const price = useMemo(() => {
    if (selectedSizeInfo?.prices?.length) {
      return selectedSizeInfo.prices[0];
    }

    if (!selectedSizeInfo) {
      const priceRange = getStylePriceRange(selectedColorVariant?.sizeInfos);
      if (priceRange) {
        return priceRange;
      }
    }

    if (selectedColorVariant?.prices?.length) {
      return selectedColorVariant?.prices[0];
    }

    return item?.price ?? {};
  }, [item, selectedColorVariant, selectedSizeInfo]);

  const instoreStockNotAvailable = useMemo(
    () =>
      selectedSizeInfo?.instoreQuantity === null ||
      selectedSizeInfo?.instoreQuantity === undefined,
    [selectedSizeInfo?.instoreQuantity]
  );

  const onlineStockNotAvailable = useMemo(
    () =>
      selectedSizeInfo?.onlineQuantity === null ||
      selectedSizeInfo?.onlineQuantity === undefined,
    [selectedSizeInfo?.onlineQuantity]
  );

  const colorDescription = useMemo(() => {
    const mainColorName = selectedColorVariant?.color.mainColorName;
    const colorVariantName = selectedColorVariant?.color.name;

    const colors = [];

    if (mainColorName && mainColorName.toLowerCase() !== COLOR_DEFAULT) {
      colors.push(mainColorName);
    }

    if (colorVariantName && colorVariantName.toLowerCase() !== COLOR_DEFAULT) {
      colors.push(colorVariantName);
    }

    return colors.join(" / ");
  }, [selectedColorVariant]);

  const addToBasketButtonContent = useMemo(() => {
    let icon = (
      <IconBasket className={classcat([css.basketIcon, "text-label-2xl"])} />
    );
    let text = t("add to basket");

    if (showOutOfStockAlarm) {
      icon = (
        <IconExclamationCircle
          className={classcat([css.basketIcon, "text-label-2xl"])}
        />
      );
      text = t("out of stock");
    } else if (isAdded) {
      icon = (
        <IconTickCircle
          className={classcat([css.basketIcon, "text-label-2xl"])}
        />
      );
      text = t("added!");
    }

    return (
      <>
        {icon} {text}
      </>
    );
  }, [t, showOutOfStockAlarm, isAdded]);

  const primaryStyleFilters = useMemo(() => {
    if (!styleDetails || !attributes) {
      return [];
    }

    const primary = attributes.filter((attr) => attr.primary);
    const styleAttributes = getAttributesWithStyleLevelAttributes(styleDetails);

    const primaryMap = new Map();
    primary.forEach((attr) => primaryMap.set(attr.key, attr));

    return styleAttributes
      .filter((attr) => primaryMap.has(attr.key))
      .map((styleAttr) => ({
        key: styleAttr.key,
        value: styleAttr.value,
        type: primaryMap.get(styleAttr.key)?.type,
        label: styleAttr.value,
        isPrimary: true,
      }));
  }, [styleDetails, attributes]);

  const showStoreFavoritesButton = useMemo(
    () => collections?.items.filter((item) => item.id).length > 0,
    [collections]
  );

  const handleScrollIntoViewContainer = useCallback(() => {
    containerRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  const scrollFromWidgetToContainer = useCallback(() => {
    if (item.isPDPOpen) {
      handleScrollIntoViewContainer();
      return;
    }

    const newSections = sections.map((section) => {
      if (section.id === sectionId) {
        return {
          ...section,
          items: section.items.map((contentItem, index) => {
            if (
              index === itemIndex &&
              contentItem.type === ContentItemType.ProductDetailsItem
            ) {
              return {
                ...contentItem,
                content: {
                  ...contentItem.content,
                  isPDPOpen: true,
                },
              };
            }

            return contentItem;
          }),
        };
      }

      return section;
    });

    updateViewState({
      sections: newSections,
    });

    handleScrollIntoViewContainer();
  }, [
    handleScrollIntoViewContainer,
    sections,
    item,
    itemIndex,
    sectionId,
    updateViewState,
  ]);

  const handleSimilarUpdate = useCallback(
    async ({
      size,
      length,
      colorVariantId,
      selectionType,
    }: {
      size?: string;
      length?: string;
      colorVariantId?: string;
      selectionType?: string;
    }) => {
      const newSize = size || selectedSize;
      const newLength = length || selectedLength;
      const newSelectedColorVariant = colorVariantId
        ? styleDetails?.variants.find(({ id }) => id === colorVariantId)
        : selectedColorVariant;

      let selectedSizeInfo;

      if (isLengthRequired) {
        selectedSizeInfo = newSelectedColorVariant?.sizeInfos?.find(
          (sizeInfo) =>
            sizeInfo.size === newSize && sizeInfo.length === newLength
        );
      } else {
        selectedSizeInfo = newSelectedColorVariant?.sizeInfos?.find(
          (sizeInfo) => sizeInfo.size === newSize
        );
      }

      if (selectedSizeInfo) {
        const basketItem = basketItems?.find(
          (item) => item.ean === selectedSizeInfo.ean
        );

        selectedSizeInfo = {
          ...selectedSizeInfo,
          availableToOrder:
            selectedSizeInfo.onlineQuantity - (basketItem?.quantity || 0),
        };
      }

      if (selectedSizeInfo?.availableToOrder === 0) {
        trackEvent({
          eventType: TrackingEventTypes.SelectOutOfStock,
          data: {
            ean: selectedSizeInfo.ean,
            styleId: item.styleId,
            variantId: colorVariantId || selectedColorVariantId,
          },
        });
      }

      const ean = selectedSizeInfo?.ean || "";

      const similarProductsWidgetIndex = widgets.findIndex(
        (item) => item.type === WidgetType.SimilarProducts
      );

      const similarProductsWidget = widgets[similarProductsWidgetIndex];

      trackEvent({
        eventType: SELECTION_COLOR_VARIANT_INFO[selectionType],
        data: {
          styleId: item.styleId,
          variantId: colorVariantId || selectedColorVariantId,
          ...(!!ean && { ean }),
        },
      });

      if (isLengthRequired && (!newLength || !newSize)) {
        return;
      }

      const sizesAndLengths = getSizesAsFilters(newSize, newLength);
      const filters = [...sizesAndLengths, ...primaryStyleFilters];

      const newWidgetItems = await getWidget({
        type: WidgetType.SimilarProducts,
        styleId: item.styleId,
        variantId: colorVariantId || selectedColorVariantId,
        ...(!!ean && {
          ean,
        }),
        ...(filters?.length && { filters }),
      });

      if (newWidgetItems && similarProductsWidget) {
        const newSimilarProductsWidget = createWidgetItem({
          type: WidgetType.SimilarProducts,
          items: newWidgetItems.map((item) => ({
            ...item,
            preselectedSize: { ean, size: newSize, length: newLength },
          })),
          attributes: {
            isExpanded: similarProductsWidget.isExpanded,
            showAllItems: similarProductsWidget.showAllItems,
          },
        });

        setWidgets((widgetsState) => {
          const filteredWidgets = widgetsState.filter(
            (w) => w.type !== WidgetType.SimilarProducts
          );

          const newWidgets = [
            ...filteredWidgets,
            newSimilarProductsWidget,
          ].sort(sortWidgets);

          return newWidgets;
        });
      }
    },
    [
      basketItems,
      getWidget,
      isLengthRequired,
      item,
      selectedColorVariant,
      selectedColorVariantId,
      selectedLength,
      selectedSize,
      styleDetails,
      widgets,
      primaryStyleFilters,
    ]
  );

  const debouncedSimilarUpdate = useDebouncedCallback(
    async ({
      size,
      length,
      colorVariantId,
      selectionType,
    }: {
      size?: string | null;
      length?: string | null;
      colorVariantId?: string;
      selectionType?: string;
    }) => {
      await handleSimilarUpdate({
        size,
        length,
        colorVariantId,
        selectionType,
      });
    },
    1000
  );

  const handleSelectSize = useCallback(
    async (newSize: string) => {
      let size = newSize;
      if (selectedSize === size) {
        size = null;
      }
      setSelectedSize(size);
      const { lengths } = getSizesAndLengths(size);
      if (lengths.length === 1) {
        setSelectedLength(lengths[0]);
      } else {
        setSelectedLength(null);
      }

      updateViewState({
        showSearchAndFooterDrawers: false,
      });

      await debouncedSimilarUpdate({ size, selectionType: "size" });
    },
    [selectedSize, getSizesAndLengths, updateViewState, debouncedSimilarUpdate]
  );

  const handleSelectLength = useCallback(
    async (newLength: string) => {
      let length = newLength;
      if (selectedLength === length) {
        length = null;
      }
      setSelectedLength(length);

      updateViewState({
        showSearchAndFooterDrawers: false,
      });

      await debouncedSimilarUpdate({ length, selectionType: "length" });
    },
    [debouncedSimilarUpdate, selectedLength, updateViewState]
  );

  const handleSelectColorVariant = useCallback(
    async (event: React.SyntheticEvent<HTMLDivElement>) => {
      const newVariantId = event.currentTarget.dataset.id;

      updateViewState({
        showSearchAndFooterDrawers: false,
      });

      if (selectedColorVariantId === newVariantId) {
        return;
      }

      setSelectedColorVariantId(newVariantId);

      handleScrollIntoViewContainer();

      await handleSimilarUpdate({
        colorVariantId: newVariantId,
        selectionType: "colorVariantId",
      });

      setShowSizeError(false);
    },
    [
      updateViewState,
      selectedColorVariantId,
      handleSimilarUpdate,
      handleScrollIntoViewContainer,
    ]
  );

  const handleBasketClick = useCallback(
    async (event: SyntheticEvent<HTMLButtonElement>) => {
      const savedTarget = event.currentTarget;

      updateViewState({
        showSearchAndFooterDrawers: false,
      });

      if (showOutOfStockAlarm) {
        return;
      }

      if (!selectedSizeInfo) {
        setShowSizeError(true);
        return;
      }

      if (isPending || selectedSizeInfo.availableToOrder === 0) {
        if (selectedSizeInfo.availableToOrder === 0) {
          setShowOutOfStockAlarm(true);

          setTimeout(
            () => setShowOutOfStockAlarm(false),
            BASKET_BUTTON_CHANGE_STATE_TIMEOUT
          );

          trackEvent({
            eventType: TrackingEventTypes.FailedAddingToBasketDueToStockLimit,
            data: {
              ean: selectedSizeInfo.ean,
              quantity: 1,
              styleId: item.styleId,
              variantId: selectedColorVariantId,
            },
          });
        }
        return;
      }

      const hasBasketItem = basketItems?.some(
        (basketItem) => basketItem.ean === selectedSizeInfo.ean
      );

      const preparedBasketItems =
        basketItems?.map((item) => ({
          ean: item.ean,
          productId: item.productId,
          price: item.price,
          quantity: item.quantity,
        })) || [];

      const newBasketItems = hasBasketItem
        ? preparedBasketItems.map((item) => {
            if (item.ean === selectedSizeInfo.ean) {
              return {
                ...item,
                quantity: item.quantity + 1,
              };
            }

            return item;
          })
        : [
            ...preparedBasketItems,
            {
              ean: selectedSizeInfo.ean,
              productId: item.styleId,
              quantity: 1,
              price: item.price as BasketItemPrice,
            },
          ];

      const updateBasketResult = await updateBasket({
        items: newBasketItems,
      });

      trackEvent({
        eventType: TrackingEventTypes.BasketQuantityChanged,
        data: {
          ean: selectedSizeInfo.ean,
          quantity: 1,
          styleId: item.styleId,
          variantId: selectedColorVariantId,
        },
      });

      if (!updateBasketResult) {
        return;
      }

      setIsAdded(true);
      if (
        isPortraitView ||
        (isLandscapeView &&
          window.innerWidth > SCREEN_BREAKPOINTS.BIG_SCREEN_LANDSCAPE)
      ) {
        const buttonRect = savedTarget.getBoundingClientRect();

        // center the animation overlay on the button
        setTimeout(() => {
          animationOverlayRef.current.style.display = "block";

          api.set({
            top: buttonRect.top - ADDITIONAL_TOP_OFFSET,
            left:
              buttonRect.left +
              Math.round(buttonRect.width / 2) -
              animationOverlayRef.current.clientWidth / 2,
          });

          api.start({
            top: convertSizeBasedOnResolution(60),
            left: window.innerWidth - convertSizeBasedOnResolution(100),
          });
        }, ANIMATION_OVERLAY_TIMEOUT);
      }

      setTimeout(() => {
        setIsAdded(false);

        animationOverlayRef.current.style.display = "none";
      }, BASKET_BUTTON_CHANGE_STATE_TIMEOUT);

      howToWearItRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    },
    [
      updateViewState,
      isLandscapeView,
      isPortraitView,
      showOutOfStockAlarm,
      selectedSizeInfo,
      isPending,
      basketItems,
      item.styleId,
      item.price,
      updateBasket,
      api,
      selectedColorVariantId,
    ]
  );

  const showUnavailableSlash = useMemo(() => {
    if (isLengthRequired) {
      return selectedSize && selectedLength;
    }

    return !!selectedSize;
  }, [isLengthRequired, selectedLength, selectedSize]);

  const handleShowMoreWidgetItems = useCallback((widgetId: string) => {
    setWidgets((widgetsState) =>
      widgetsState.map((widget) =>
        widget.id === widgetId
          ? {
              ...widget,
              isExpanded: true,
            }
          : widget
      )
    );
  }, []);

  const handleShowAllWidgetItems = useCallback((widgetId: string) => {
    setWidgets((widgetsState) =>
      widgetsState.map((widget) =>
        widget.id === widgetId ? { ...widget, showAllItems: true } : widget
      )
    );
  }, []);

  const removeCurrentStyleFromSection = useCallback(() => {
    const newSections = sections.map((section) => {
      if (section.id === sectionId) {
        const filteredItems = [...section.items];
        filteredItems.splice(itemIndex, 1);

        return {
          ...section,
          items: filteredItems,
        };
      }

      return section;
    });

    setTimeout(
      () =>
        updateViewState({
          sections: newSections,
        }),
      ANIMATION_CLOSE_PDP
    );
  }, [itemIndex, sectionId, updateViewState, sections]);

  const handleClosePDP = useCallback(() => {
    const noWidgets = widgets.length === 0;
    const widgetsWereExpanded = widgets.some((widget) => widget.isExpanded);

    const newSections = sections.map((section) => {
      if (section.id === sectionId) {
        return {
          ...section,
          items: section.items.map((contentItem, index) => {
            if (
              index === itemIndex &&
              contentItem.type === ContentItemType.ProductDetailsItem
            ) {
              return {
                ...contentItem,
                content: {
                  ...contentItem.content,
                  isPDPOpen: false,
                  isWidgetsOpen:
                    noWidgets || !widgetsWereExpanded
                      ? false
                      : contentItem.content.isWidgetsOpen,
                },
              };
            }

            return contentItem;
          }),
        };
      }

      return section;
    });

    updateViewState({
      sections: newSections,
    });

    if (noWidgets) {
      removeCurrentStyleFromSection();
      return;
    }

    const newWidgets = widgets.map((widget) => ({
      ...widget,
      isClosed: !widget.isExpanded,
    }));

    setWidgets(newWidgets);

    if (newWidgets.every((widget) => widget.isClosed)) {
      removeCurrentStyleFromSection();
    }
  }, [
    widgets,
    sections,
    sectionId,
    itemIndex,
    updateViewState,
    removeCurrentStyleFromSection,
  ]);

  const handleCloseWidget = useCallback(
    (event: SyntheticEvent) => {
      const widgetId = event.currentTarget.getAttribute("id");

      const newWidgets = widgets.map((widget) => ({
        ...widget,
        isClosed: widget.id === widgetId ? true : widget.isClosed,
      }));

      setWidgets(newWidgets);

      if (newWidgets.every((widget) => widget.isClosed)) {
        const newSections = sections.map((section) => {
          if (section.id === sectionId) {
            return {
              ...section,
              items: section.items.map((contentItem, index) => {
                if (
                  index === itemIndex &&
                  contentItem.type === ContentItemType.ProductDetailsItem
                ) {
                  return {
                    ...contentItem,
                    content: {
                      ...contentItem.content,
                      isWidgetsOpen: false,
                    },
                  };
                }

                return contentItem;
              }),
            };
          }

          return section;
        });

        updateViewState({
          sections: newSections,
        });

        if (!item.isPDPOpen) {
          removeCurrentStyleFromSection();
        }
      }
    },
    [
      item,
      itemIndex,
      removeCurrentStyleFromSection,
      sectionId,
      sections,
      updateViewState,
      widgets,
    ]
  );

  const handleFavoritesClick = useCallback(() => {
    setIsCollectionModalOpen(true);

    trackEvent({
      eventType: TrackingEventTypes.AddToCollectionModalOpened,
      data: {
        styleId: item.styleId,
        variantId: selectedColorVariantId,
      },
    });
  }, [item, selectedColorVariantId]);

  const handleCollectionModalClose = useCallback(() => {
    setIsCollectionModalOpen(false);
  }, []);

  const styleHasScannedEAN = useMemo(
    () =>
      item?.styleDetails?.variants
        .reduce((previous, current) => [...previous, ...current.sizeInfos], [])
        .some(({ ean }) => ean === lastScannedValue),
    [item, lastScannedValue]
  );

  const COLOR_VARIANTS_IN_ROW_LIMIT = useMemo(() => {
    const innerWidth = window.innerWidth;

    let contentWidth = innerWidth;

    if (isLandscapeView) {
      switch (true) {
        case innerWidth > SCREEN_BREAKPOINTS.BIG_SCREEN_LANDSCAPE:
          contentWidth = innerWidth - SEARCH_DRAWER_WIDTH.BIG_SCREEN_LANDSCAPE;
          break;
        case innerWidth > SCREEN_BREAKPOINTS.MEDIUM_SCREEN_LANDSCAPE:
          contentWidth =
            innerWidth - SEARCH_DRAWER_WIDTH.MEDIUM_SCREEN_LANDSCAPE;
          break;
        default:
          contentWidth =
            innerWidth - SEARCH_DRAWER_WIDTH.SMALL_SCREEN_LANDSCAPE;
      }
    }

    return Math.floor(
      (contentWidth - (COLOR_VARIANTS_SIDEMARGIN * 2 - COLOR_VARIANTS_GAP)) /
        (COLOR_VARIANT_WIDTH + COLOR_VARIANTS_GAP)
    );
  }, [isLandscapeView]);

  const colorVariants = useMemo(() => {
    const colorVariants = styleDetails?.variants ?? [];
    const variantWithMedia = colorVariants.map((colorVariant) => ({
      id: colorVariant.id,
      media: sortProductMedia(
        colorVariant.media,
        MediaOrderingRule.ColorThumbnails
      )[0],
    }));

    if (
      variantWithMedia.length >
        COLOR_VARIANTS_IN_ROW_LIMIT * COLOR_VARIANTS_INITIAL_ROWS &&
      !showMoreColorVariants
    ) {
      return variantWithMedia.slice(
        0,
        COLOR_VARIANTS_IN_ROW_LIMIT * COLOR_VARIANTS_INITIAL_ROWS - 1
      );
    } else {
      return variantWithMedia;
    }
  }, [
    styleDetails?.variants,
    showMoreColorVariants,
    COLOR_VARIANTS_IN_ROW_LIMIT,
  ]);

  const handleShowMoreColorVariants = useCallback(
    () => setShowMoreColorVariants(true),
    []
  );

  const colorVariantsContainerHeight = useMemo(() => {
    if (!colorVariants) {
      return 0;
    }

    const colorVariantsRows =
      Math.floor(colorVariants?.length / COLOR_VARIANTS_IN_ROW_LIMIT) + 1;

    const ImageHeightWithNearestGap = COLOR_VARIANT_HEIGHT + COLOR_VARIANTS_GAP;

    return colorVariantsRows * ImageHeightWithNearestGap - COLOR_VARIANTS_GAP;
  }, [colorVariants, COLOR_VARIANTS_IN_ROW_LIMIT]);

  useEffect(() => {
    if (
      styleDetails?.variants?.length <=
      COLOR_VARIANTS_IN_ROW_LIMIT * COLOR_VARIANTS_INITIAL_ROWS
    ) {
      setShowMoreColorVariants(true);
    }
  }, [styleDetails?.variants?.length, COLOR_VARIANTS_IN_ROW_LIMIT]);

  useEffect(() => {
    const fetchWidgets = async () => {
      if (!styleDetails || !attributes) {
        return;
      }

      const widgets = [];

      const preselectedSize = item.preselectedSize
        ? {
            ean: item.preselectedSize.ean,
            size: item.preselectedSize?.size,
            ...(item.preselectedSize?.length && {
              length: item.preselectedSize?.length,
            }),
          }
        : null;

      const sizesAndLengths = getSizesAsFilters(
        preselectedSize?.size,
        preselectedSize?.length
      );
      const filters = [...sizesAndLengths, ...primaryStyleFilters];

      const similarWidget = await getWidget({
        type: WidgetType.SimilarProducts,
        styleId: item.styleId,
        variantId: selectedColorVariantId,
        ...preselectedSize,
        ...(filters.length && !styleHasScannedEAN && { filters }),
      });

      if (similarWidget) {
        widgets.push(
          createWidgetItem({
            type: WidgetType.SimilarProducts,
            items: similarWidget.map((widgetItem) => ({
              ...widgetItem,
              ...(preselectedSize && {
                preselectedSize,
              }),
            })),
          })
        );
      }

      if (widgets.length) {
        setWidgets((widgetsState) => {
          if (widgetsState.length) {
            const newWidgets = [...widgetsState, ...widgets].sort(sortWidgets);
            return newWidgets;
          }

          return widgets;
        });
      }
    };

    fetchWidgets();
  }, [styleDetails]);

  useEffect(() => {
    const fetchWidgets = async () => {
      if (!styleDetails || !attributes) {
        return;
      }

      const filters = primaryStyleFilters;
      const widgets = [];

      const howToWearItWidgets = await getHTWIWidget(
        selectedColorVariantId,
        item.styleId,
        filters
      );

      if (howToWearItWidgets?.length) {
        widgets.push(
          createWidgetItem({
            type: WidgetType.HowToWearIt,
            items: howToWearItWidgets,
          })
        );
      }

      setWidgets((widgetsState) => {
        if (widgetsState.length) {
          const filteredWidgets = widgetsState.filter(
            (w) => w.type !== WidgetType.HowToWearIt
          );

          const newWidgets = [...filteredWidgets, ...widgets].sort(sortWidgets);
          return newWidgets;
        }

        return widgets;
      });
    };

    fetchWidgets();
  }, [styleDetails, selectedColorVariantId]);

  useEffect(() => {
    if (showSizeError && selectedSizeInfo) {
      setShowSizeError(false);
    }
  }, [selectedSizeInfo, showSizeError]);

  useEffect(() => {
    setShowOutOfStockAlarm(false);
  }, [selectedSizeInfo]);

  useEffect(() => {
    trackEvent({
      eventType: TrackingEventTypes.ProductDetailsOpen,
      data: {
        styleId: item.styleId,
        variantId: item.id,
      },
    });
  }, []);

  return {
    styleDetails,
    colorVariants,
    colorVariantsCollapsedLimit:
      COLOR_VARIANTS_IN_ROW_LIMIT * COLOR_VARIANTS_INITIAL_ROWS,
    colorDescription,
    sizes,
    lengths,
    instoreStockNotAvailable,
    onlineStockNotAvailable,
    selectedSize,
    selectedLength,
    selectedSizeInfo,
    selectedColorVariant,
    showSizeError,
    animationOverlayRef,
    styles,
    handleSelectColorVariant,
    handleSelectSize,
    handleSelectLength,
    handleBasketClick,
    handleShowMoreColorVariants,
    isPending,
    t,
    showOutOfStockAlarm,
    addToBasketButtonContent,
    isAdded,
    containerRef,
    howToWearItRef,
    widgets,
    handleScrollIntoViewContainer,
    scrollFromWidgetToContainer,
    handleShowMoreWidgetItems,
    handleShowAllWidgetItems,
    isLastItem,
    price,
    showMoreColorVariants,
    colorVariantsContainerHeight,
    isCheckoutEnabled,
    isCollectionModalOpen,
    handleClosePDP,
    handleCloseWidget,
    handleFavoritesClick,
    handleCollectionModalClose,
    notOnlyItemInSection,
    availableSizesForColorVariant,
    availableLengthsForColorVariant,
    showUnavailableSlash,
    availableVariantsWithSelectedSize,
    eventData: {
      styleId: item.styleId,
      variantId: selectedColorVariantId,
      ...(selectedSizeInfo && { ean: selectedSizeInfo.ean }),
    },
    materialCompositionAndProductDescriptionEventData,
    showStoreFavoritesButton,
  };
};

export default useManageProductDetails;
